import React from "react";
import { setWidgetDisplay } from "../db/action";
import { WidgetDisplay } from "../db/state";

const EDGE_PADDING = 8;

type Position = { x: number; y: number };

const Widget: React.FC<React.PropsWithChildren<WidgetDisplay>> = ({
  children,
  colour,
  fontFamily,
  fontSize = 16,
  fontWeight,
  className = "",
  x = 0,
  y = 0,
  id,
}) => {
  const keepCentered = className.includes("dashboard__widget--keep-centered");
  const anchorBottom = className.includes("dashboard__widget--anchor-bottom");
  const anchorTopRight = className.includes("dashboard__widget--anchor-top-right");
  const setDisplay = React.useMemo(() => setWidgetDisplay.bind(null, id), [id]);
  const nodeRef = React.useRef<HTMLDivElement>(null);

  const [pos, setPos] = React.useState<Position>(() => ({ x, y }));
  const posRef = React.useRef(pos);
  posRef.current = pos;

  // Sticky anchoring refs
  const offsetX = React.useRef(0);
  const anchorBottomGap = React.useRef(48);

  // ----- reposition helper (clamp + persist) --------------------------------
  //
  // IMPORTANT: for keepCentered+anchorBottom widgets the absolute x/y stored in
  // the DB is meaningless — the ideal position is always computed from the
  // element's current dimensions.  Persisting every ResizeObserver-triggered
  // reposition causes a race where a "loading" (small) position can overwrite
  // the correct "verse loaded" (large) position in IndexedDB before the
  // beforeunload flush fires.  Only user drags should persist for those widgets.
  const reposition = React.useCallback(
    (fromX: number, fromY: number, persist = true) => {
      const el = nodeRef.current;
      if (!el) return;

      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const cx = Math.round((window.innerWidth - w) / 2);
      const nx = Math.min(
        Math.max(fromX, EDGE_PADDING),
        Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING),
      );
      const ny = Math.min(
        Math.max(fromY, EDGE_PADDING),
        Math.max(EDGE_PADDING, window.innerHeight - h - EDGE_PADDING),
      );

      offsetX.current = nx - cx;
      anchorBottomGap.current = window.innerHeight - h - ny;

      const clamped: Position = { x: nx, y: ny };
      setPos(clamped);
      posRef.current = clamped;
      if (persist && (clamped.x !== x || clamped.y !== y)) {
        setDisplay(clamped);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDisplay],
  );

  // ----- compute ideal position (centred horizontally, near bottom) ----------
  const computeIdeal = React.useCallback(
    (): Position | null => {
      const el = nodeRef.current;
      if (!el) return null;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!w || !h) return null;
      return {
        x: Math.round((window.innerWidth - w) / 2),
        y: Math.max(EDGE_PADDING, window.innerHeight - h - 40),
      };
    },
    [],
  );

  // ----- initialise + re-centre on content-size change ----------------------
  const inited = React.useRef(false);
  React.useEffect(() => {
    const el = nodeRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!w || !h) return;

      if (!inited.current) {
        // First paint — ensure EVERY widget is within the viewport
        inited.current = true;

        if (anchorTopRight) {
          // Weather: pin to top-right corner
          const nx = Math.max(EDGE_PADDING, window.innerWidth - w - 24);
          const ny = 24;
          reposition(nx, ny);
        } else if (keepCentered && anchorBottom) {
          // Verse: always centre horizontally near the bottom.
          // Stored x/y may be stale (based on wrong assumed dimensions), so
          // compute ideal position from actual element size every time.
          // Don't persist — the absolute position is computed, not user-set.
          const ideal = computeIdeal();
          if (ideal) reposition(ideal.x, ideal.y, false);
        } else {
          // All other widgets: just clamp to viewport boundaries
          const nx = Math.min(
            Math.max(x, EDGE_PADDING),
            Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING),
          );
          const ny = Math.min(
            Math.max(y, EDGE_PADDING),
            Math.max(EDGE_PADDING, window.innerHeight - h - EDGE_PADDING),
          );
          if (nx !== x || ny !== y) reposition(nx, ny);
        }
      } else {
        // Content changed (new verse, font size, locale, mode, etc.)
        // → always re-centre near bottom, regardless of stored position
        if (keepCentered && anchorBottom) {
          const ideal = computeIdeal();
          if (ideal) reposition(ideal.x, ideal.y, false);
        }
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- sync external x/y -------------------------------------------------
  React.useEffect(() => {
    if (posRef.current.x !== x || posRef.current.y !== y) {
      const next = { x, y };
      setPos(next);
      posRef.current = next;
    }
  }, [x, y]);

  // ----- resize ------------------------------------------------------------
  React.useEffect(() => {
    const onResize = () => {
      const el = nodeRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;

      let nx: number;
      if (keepCentered) {
        const cx = Math.round((window.innerWidth - w) / 2);
        nx = cx + offsetX.current;
      } else if (anchorTopRight) {
        nx = window.innerWidth - w - 24;
      } else {
        nx = posRef.current.x;
      }
      nx = Math.min(Math.max(nx, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING));

      let ny: number;
      if (anchorBottom) {
        ny = window.innerHeight - h - anchorBottomGap.current;
      } else if (anchorTopRight) {
        ny = 24;
      } else {
        ny = posRef.current.y;
      }
      ny = Math.min(Math.max(ny, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerHeight - h - EDGE_PADDING));

      const clamped = { x: nx, y: ny };
      setPos(clamped);
      posRef.current = clamped;
      setDisplay(clamped);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [keepCentered, anchorBottom, anchorTopRight, setDisplay]);

  // ----- native drag (raw DOM events, bypass React synthetic) ---------------
  const [dragging, setDragging] = React.useState(false);
  // Keep ref copies of all props/state that event handlers need
  const dragPropsRef = React.useRef({ keepCentered, anchorBottom, setDisplay });
  dragPropsRef.current = { keepCentered, anchorBottom, setDisplay };

  React.useEffect(() => {
    const el = nodeRef.current;
    if (!el) return;

    let drag: { sx: number; sy: number; px: number; py: number } | null = null;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType !== "touch") return;
      el.setPointerCapture(e.pointerId);
      e.preventDefault();
      drag = { sx: e.clientX, sy: e.clientY, px: posRef.current.x, py: posRef.current.y };
      setDragging(true);
    };

    const onMove = (e: PointerEvent) => {
      if (!drag) return;
      e.preventDefault();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      let nx = drag.px + (e.clientX - drag.sx);
      let ny = drag.py + (e.clientY - drag.sy);
      nx = Math.min(Math.max(nx, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING));
      ny = Math.min(Math.max(ny, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerHeight - h - EDGE_PADDING));
      const next = { x: nx, y: ny };
      setPos(next);
      posRef.current = next;
    };

    const onUp = (e: PointerEvent) => {
      if (!drag) return;
      el.releasePointerCapture(e.pointerId);
      e.preventDefault();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      let nx = drag.px + (e.clientX - drag.sx);
      let ny = drag.py + (e.clientY - drag.sy);
      nx = Math.min(Math.max(nx, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING));
      ny = Math.min(Math.max(ny, EDGE_PADDING), Math.max(EDGE_PADDING, window.innerHeight - h - EDGE_PADDING));

      if (dragPropsRef.current.keepCentered) {
        offsetX.current = nx - Math.round((window.innerWidth - w) / 2);
      }
      if (dragPropsRef.current.anchorBottom) {
        anchorBottomGap.current = window.innerHeight - h - ny;
      }

      const next = { x: nx, y: ny };
      setPos(next);
      posRef.current = next;
      dragPropsRef.current.setDisplay({ x: nx, y: ny });
      drag = null;
      setDragging(false);
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    // Also listen on window for cases where pointer leaves the element
    el.addEventListener("lostpointercapture", onUp);

    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("lostpointercapture", onUp);
    };
    // Empty deps — everything accessible via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={nodeRef}
      className={`Widget ${fontWeight ? "weight-override" : ""} widget-draggable ${className}`}
      style={{
        color: colour || "rgba(255, 255, 255, 0.96)",
        fontFamily,
        fontSize: `${fontSize}px`,
        fontWeight,
        cursor: dragging ? "grabbing" : "grab",
        position: "fixed",
        left: pos.x,
        top: pos.y,
        zIndex: 20,
        pointerEvents: "auto",
        width: keepCentered ? "fit-content" : "max-content",
        maxWidth: keepCentered ? "92vw" : "90%",
        minWidth: 0,
        overflowWrap: "break-word",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {children}
    </div>
  );
};

export default Widget;
